import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StarRating } from "@/components/StarRating";
import { toast } from "sonner";
import { Camera, Upload, X, CheckCircle, User } from "lucide-react";
import { format } from "date-fns";
import api, { getImageUrl } from "@/lib/api";

export const ReviewSection = ({ tourId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/tours/${tourId}/reviews`).then(r => {
      setReviews(Array.isArray(r.data) ? r.data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [tourId]);

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload-review-photo", formData);
      setPhotos([...photos, res.data.url]);
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!name || !email || !rating) {
      toast.error("Please fill in name, email, and rating");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/reviews", {
        tour_id: tourId, customer_email: email, customer_name: name,
        rating, title, comment, photos,
      });
      setReviews([res.data, ...reviews]);
      setFormOpen(false);
      setName(""); setEmail(""); setRating(0); setTitle(""); setComment(""); setPhotos([]);
      toast.success("Review submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-12" data-testid="review-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-cormorant text-2xl font-light text-midnight">Guest Reviews</h3>
          {reviews.length > 0 && (
            <div className="flex items-center gap-3 mt-2">
              <StarRating rating={Math.round(parseFloat(avgRating))} readOnly size="sm" />
              <span className="font-manrope text-sm text-midnight/60">
                {avgRating} out of 5 ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          )}
        </div>
        <Button
          data-testid="write-review-btn"
          onClick={() => setFormOpen(true)}
          variant="outline"
          className="rounded-full font-manrope text-xs border-sandy/30 text-sandy hover:bg-sandy/5"
        >
          Write a Review
        </Button>
      </div>

      {/* Reviews List */}
      {reviews.length === 0 && !loading && (
        <p className="font-manrope text-sm text-midnight/40 py-8" data-testid="no-reviews-msg">
          No reviews yet. Be the first to share your experience!
        </p>
      )}
      <div className="space-y-6">
        {reviews.map((review) => (
          <div key={review.review_id} className="pb-6 border-b border-midnight/5 last:border-0" data-testid={`review-${review.review_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-midnight/5 flex items-center justify-center">
                  <User className="w-4 h-4 text-midnight/30" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-manrope text-sm font-medium text-midnight">{review.customer_name}</p>
                    {review.verified && (
                      <Badge className="bg-canopy/10 text-canopy font-manrope text-[10px] py-0 px-1.5">
                        <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Verified
                      </Badge>
                    )}
                  </div>
                  <p className="font-manrope text-[10px] text-midnight/30">
                    {review.created_at ? format(new Date(review.created_at), "MMM d, yyyy") : ""}
                  </p>
                </div>
              </div>
              <StarRating rating={review.rating} readOnly size="sm" />
            </div>
            {review.title && <p className="font-manrope text-sm font-medium text-midnight mb-1">{review.title}</p>}
            {review.comment && <p className="font-manrope text-sm text-midnight/60 leading-relaxed">{review.comment}</p>}
            {review.photos?.length > 0 && (
              <div className="flex gap-2 mt-3">
                {review.photos.map((photo, i) => (
                  <div key={i} className="w-20 h-16 overflow-hidden">
                    <img src={getImageUrl(photo)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Review Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="review-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl font-light text-midnight">Write a Review</DialogTitle>
            <DialogDescription className="font-manrope text-sm text-midnight/50">
              Share your experience. A verified booking email is required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Your Rating *</Label>
              <div className="mt-2"><StarRating rating={rating} onRate={setRating} size="lg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Name *</Label>
                <Input data-testid="review-name-input" value={name} onChange={e => setName(e.target.value)} className="mt-1 font-manrope text-sm" />
              </div>
              <div>
                <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Booking Email *</Label>
                <Input data-testid="review-email-input" value={email} onChange={e => setEmail(e.target.value)} type="email" className="mt-1 font-manrope text-sm" />
              </div>
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Title</Label>
              <Input data-testid="review-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Summarize your experience" className="mt-1 font-manrope text-sm" />
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Your Experience</Label>
              <Textarea data-testid="review-comment-input" value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder="Tell others about your adventure..." className="mt-1 font-manrope text-sm" />
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50 mb-2 block">Photos</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-16 h-12 overflow-hidden border border-midnight/10">
                    <img src={getImageUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 p-0.5"><X className="w-2.5 h-2.5 text-white" /></button>
                  </div>
                ))}
              </div>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-midnight/10 cursor-pointer hover:border-sandy transition-colors duration-200 text-midnight/50">
                <Camera className="w-3 h-3" />
                <span className="font-manrope text-xs">{uploading ? "Uploading..." : "Add Photo"}</span>
                <input data-testid="review-photo-upload" type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
              </label>
            </div>
            <Button
              data-testid="review-submit-btn"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full rounded-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm h-10"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
